import React from 'react';
import PageBar, {
  PageBarCrumb,
  PageBarIconAction,
  PageBarMenuItem,
} from '../PageBar';

interface DocumentBarProps {
  /** Document filename used for the leaf crumb. */
  originalFilename: string;
  /** Family-member id (used to compose the "back to member" link in
   *  both branches; the member dossier is reached via `/members/:id`
   *  regardless of own-family vs shared-member viewing context). */
  familyMemberId: number;
  memberLabel: string;
  /** Family id is only consulted in the own-family branch (to build
   *  the `/families/:id` crumb); ignored when `viewedAsSharedMember`
   *  is true. */
  familyId: number;
  familyName: string;
  /** When `true`, the breadcrumb collapses the family link and lands
   *  on `/shared-members` instead of the inaccessible family page
   *  (mirrors the same prop on `MemberBar`). */
  viewedAsSharedMember: boolean;

  // Primary action — download. The handler decrypts in-place when
  // needed, so the button never needs a "necesită deblocare" tooltip.
  onDownload: () => void;
  downloading: boolean;

  // Reprocess icon actions. Each carries its own busy flag so the
  // spinner pulses on the right button only; `anyBusy` is the shared
  // lockout that disables every reprocess control while one is in
  // flight (matches the existing hero behaviour).
  onReocr: () => void;
  isOcrRunning: boolean;
  onRegenerateSummary: () => void;
  isSummaryGenerating: boolean;
  onReclassify: () => void;
  isClassifying: boolean;
  anyBusy: boolean;
  /** OCR text must exist before "Regenerează rezumat" / "Reclasifică"
   *  can fire — backend rejects the request otherwise. We mirror that
   *  rule in the UI so the buttons don't appear actionable when they
   *  can't actually run. */
  ocrDone: boolean;

  // "Reprocesează tot" lives inline on the bar (leftmost reprocess
  // icon) so it mirrors the pre-PageBar hero layout. `onRerunAll` is
  // the same handler the kebab used to call; `isRerunAllInFlight`
  // pulses the rerun-all icon only while the initial POST is in
  // flight (after the 202, the per-stage spinners take over and walk
  // OCR → Summary → Classify just like before).
  onRerunAll: () => void;
  isRerunAllInFlight: boolean;

  // Kebab actions.
  onEdit: () => void;
  onDelete: () => void;
}

const IconDocument = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const IconDownload = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconRefresh = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const IconSummary = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
    <line x1="8" y1="9" x2="12" y2="9" />
  </svg>
);

const IconClassify = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
    <path d="M7 7h.01" />
  </svg>
);

const IconRerunAll = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
    <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
    <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
  </svg>
);

const IconEdit = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

const IconUser = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconTrash = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
  </svg>
);

/**
 * Truncate a long filename in the middle so the leaf crumb stays
 * readable on the bar (which only gives crumbs ~9 rem of width before
 * they ellipsize). Keeps the leading + trailing portion so the
 * extension and any version suffix (`_v2.pdf`) remain visible, with
 * an ellipsis bridging the middle:
 *
 *   `radiologie_torace_dr_popescu_20260214_v2.pdf`
 *   →
 *   `radiologie_torace_dr_…_v2.pdf`
 *
 * Names already within the budget are returned untouched.
 */
function shortFilename(name: string, max = 40): string {
  const s = (name ?? '').trim();
  if (s.length <= max) return s;
  // Reserve 1 char for the ellipsis; split the rest 60/40 (head/tail)
  // so the extension + version suffix stay visible at the end.
  const budget = max - 1;
  const headLen = Math.ceil(budget * 0.6);
  const tailLen = budget - headLen;
  return `${s.slice(0, headLen)}…${s.slice(s.length - tailLen)}`;
}

/**
 * Sticky document-bar shown above the page-container content on
 * `/documents/:documentId`.
 *
 * Thin composition layer over the reusable `<PageBar>` — feeds in the
 * document-flavoured crumbs (`Dashboard / Familii / <family> /
 * <member> / <filename>` on the own-family branch, `Dashboard / Membri
 * partajați / <member> / <filename>` on the shared branch), a small
 * "document" leading chip, the "Descarcă" primary action, the four
 * reprocess icon actions (Reprocesează tot / Re-OCR / Regenerează
 * rezumat / Reclasifică — same order as the pre-PageBar hero), and a
 * kebab menu with the remaining verbs (Editează, Vezi membrul, Șterge
 * document). All sticky-bar layout, scroll shadow, kebab open/close
 * logic etc. lives in `PageBar`.
 *
 * Delete is wired to open a `ConfirmDialog` in the parent — the bar
 * itself just calls `onDelete` and lets the page own modal state.
 */
const DocumentBar: React.FC<DocumentBarProps> = ({
  originalFilename,
  familyMemberId,
  memberLabel,
  familyId,
  familyName,
  viewedAsSharedMember,
  onDownload,
  downloading,
  onReocr,
  isOcrRunning,
  onRegenerateSummary,
  isSummaryGenerating,
  onReclassify,
  isClassifying,
  anyBusy,
  ocrDone,
  onRerunAll,
  isRerunAllInFlight,
  onEdit,
  onDelete,
}) => {
  const familyLabel = (familyName || '').trim() || 'Familie';
  const memberLink = `/members/${familyMemberId}`;
  const filenameLeaf = shortFilename(originalFilename);

  // Branch the breadcrumb on access mode. The second-to-last clickable
  // crumb is the member dossier in both branches — that's what the
  // mobile back button derives from (per the parallel worker's
  // backTo-from-crumbs change). Both branches end at the filename leaf.
  const crumbs: PageBarCrumb[] = viewedAsSharedMember
    ? [
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Membri partajați', to: '/shared-members' },
        { label: memberLabel, to: memberLink },
        { label: filenameLeaf },
      ]
    : [
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Familii', to: '/families' },
        { label: familyLabel, to: `/families/${familyId}` },
        { label: memberLabel, to: memberLink },
        { label: filenameLeaf },
      ];

  // Small document chip mirroring the folder/initials chips used by
  // FamilyDetail / MemberBar. Slate-100 background, slate-700 icon —
  // visually quiet so the breadcrumb stays the focal point.
  const leadingChip = (
    <span
      className="page-bar-crumb-chip"
      style={{
        background: 'var(--color-slate-100)',
        color: 'var(--color-slate-700)',
      }}
      aria-hidden
    >
      <IconDocument />
    </span>
  );

  // Reprocesează tot / Re-OCR / Regenerează rezumat / Reclasifică sit
  // between primary and kebab. The order mirrors the pre-PageBar
  // hero (`.doc-view-hero-icon-actions` in git history): Rerun All
  // first, then OCR → Summary → Classify. Each per-stage button shows
  // its own spinner whenever THAT stage is running, so a "Reprocesează
  // tot" click appears to walk the spinner OCR → Summary → Classify
  // as the backend progresses; the rerun-all button itself only
  // pulses while the initial POST is in flight. `anyBusy` is the
  // shared lockout that disables every reprocess control while one is
  // in flight; the post-OCR stages additionally require `ocrDone` so
  // the user can't fire them before there's text.
  const iconActions: PageBarIconAction[] = [
    {
      icon: <IconRerunAll />,
      label: 'Reprocesează tot',
      busyLabel: 'Se reprocesează tot…',
      onClick: onRerunAll,
      disabled: anyBusy,
      busy: isRerunAllInFlight,
    },
    {
      icon: <IconRefresh />,
      label: 'Reprocesare OCR',
      busyLabel: 'Se procesează OCR…',
      onClick: onReocr,
      disabled: anyBusy,
      busy: isOcrRunning,
    },
    {
      icon: <IconSummary />,
      label: 'Regenerează rezumat',
      busyLabel: 'Se regenerează rezumatul…',
      onClick: onRegenerateSummary,
      disabled: anyBusy || !ocrDone,
      busy: isSummaryGenerating,
    },
    {
      icon: <IconClassify />,
      label: 'Reclasificare AI',
      busyLabel: 'Se reclasifică…',
      onClick: onReclassify,
      disabled: anyBusy || !ocrDone,
      busy: isClassifying,
    },
  ];

  const menuItems: PageBarMenuItem[] = [
    {
      label: 'Editează detalii',
      icon: <IconEdit />,
      onClick: onEdit,
    },
    {
      label: viewedAsSharedMember ? 'Vezi membrul partajat' : 'Vezi membrul',
      icon: <IconUser />,
      to: memberLink,
    },
    {
      label: 'Șterge document',
      icon: <IconTrash />,
      onClick: onDelete,
      danger: true,
    },
  ];

  return (
    <PageBar
      ariaLabel="Navigare document"
      crumbs={crumbs}
      leadingChip={leadingChip}
      primaryAction={{
        label: 'Descarcă',
        icon: <IconDownload />,
        onClick: onDownload,
        disabled: downloading,
        ariaLabel: downloading ? 'Se descarcă documentul…' : 'Descarcă documentul',
      }}
      iconActions={iconActions}
      menuItems={menuItems}
    />
  );
};

export default DocumentBar;
